import { useFrame } from '@react-three/fiber'
import { RefObject, useRef } from 'react'
import { useRunState } from '@/src/game/provider/useRunState'
import { useTrackController } from '@/src/game/models/track/useTrackController'
import { store } from '@/src/stores/store'
import {
    type FootstepSide,
    type FootstepJumpEvent,
    type FootstepStepEvent,
    type FootstepTimelineRef,
    getCadenceInterval,
} from './footstepConfig'

export function useFootstepTimeline(
    runAnimationTimeScaleRef?: RefObject<number>,
): FootstepTimelineRef {
    const runState = useRunState()
    const trackController = useTrackController()

    const cadenceElapsedRef = useRef(0)
    const nextSideRef = useRef<FootstepSide>('left')
    const stepIdRef = useRef(0)
    const jumpIdRef = useRef(0)
    const clockRef = useRef(0)
    const wasJumpingRef = useRef(false)
    const previousJumpTriggerIdRef = useRef(runState.snapshot.jumpTriggerId)

    const timelineRef = useRef({
        isActive: false,
        footstepsEnabled: false,
        runSpeed: 0,
        animationSpeed: 1,
        isJumping: false,
        steps: [] as FootstepStepEvent[],
        jumps: [] as FootstepJumpEvent[],
    })

    useFrame((_, delta) => {
        const safeDelta = Math.min(delta, 1 / 60)
        clockRef.current += safeDelta

        const snapshot = runState.snapshot
        const playback = trackController.getTrackPlaybackState()
        const jumpTriggerId = snapshot.jumpTriggerId

        const isActive =
            store.appFlowPhase === 'run' &&
            (runState.phase === 'running' || runState.phase === 'levelTransition')

        const isJumping = snapshot.isJumping
        const isHit = snapshot.hitReactionTimer > 0
        const keepLeavesSpawningDuringJump = store.footstepEffect === 'leaves'
        const footstepsEnabled = isActive && !isHit && (keepLeavesSpawningDuringJump || !isJumping)

        const runSpeed = playback.speed
        const animationSpeed = Math.max(runAnimationTimeScaleRef?.current ?? 1, 0.001)

        const steps: FootstepStepEvent[] = []
        const jumps: FootstepJumpEvent[] = []

        if (!footstepsEnabled) {
            cadenceElapsedRef.current = 0
        } else {
            cadenceElapsedRef.current += safeDelta
            const interval = getCadenceInterval(runSpeed, animationSpeed)

            let spawnCount = 0
            while (cadenceElapsedRef.current >= interval && spawnCount < 4) {
                cadenceElapsedRef.current -= interval

                steps.push({
                    id: ++stepIdRef.current,
                    side: nextSideRef.current,
                    timestamp: clockRef.current,
                })

                nextSideRef.current = nextSideRef.current === 'left' ? 'right' : 'left'
                spawnCount++
            }
        }

        if (
            isActive &&
            (
                (isJumping && !wasJumpingRef.current) ||
                jumpTriggerId !== previousJumpTriggerIdRef.current
            )
        ) {
            jumps.push({
                id: ++jumpIdRef.current,
                timestamp: clockRef.current,
            })
        }
        wasJumpingRef.current = isJumping
        previousJumpTriggerIdRef.current = jumpTriggerId

        timelineRef.current = {
            isActive,
            footstepsEnabled,
            runSpeed,
            animationSpeed,
            isJumping,
            steps,
            jumps,
        }
    })

    return timelineRef
}

